import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Send } from 'lucide-react';

const ContactForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    country: '',
    projectTypes: [],
    numSites: '',
    urgency: '',
    message: ''
  });

  const handleProjectTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      projectTypes: prev.projectTypes.includes(type)
        ? prev.projectTypes.filter(t => t !== type)
        : [...prev.projectTypes, type]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission - will be replaced with API call
    console.log('Form submitted:', formData);
    
    toast({
      title: "Request Submitted!",
      description: "We'll contact you within 24 hours.",
    });

    // Reset form
    setFormData({
      fullName: '',
      company: '',
      email: '',
      phone: '',
      country: '',
      projectTypes: [],
      numSites: '',
      urgency: '',
      message: ''
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-900 via-blue-800 to-teal-700 text-white">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-lg p-8 md:p-12 rounded-3xl border border-white/20">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name & Company */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="fullName" className="text-white mb-2">{t.contact.form.fullName}</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>
              <div>
                <Label htmlFor="company" className="text-white mb-2">{t.contact.form.company}</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="email" className="text-white mb-2">{t.contact.form.email}</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-white mb-2">{t.contact.form.phone}</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
                />
              </div>
            </div>

            {/* Country */}
            <div>
              <Label htmlFor="country" className="text-white mb-2">{t.contact.form.country}</Label>
              <Select value={formData.country} onValueChange={(value) => setFormData({ ...formData, country: value })}>
                <SelectTrigger className="bg-white/20 border-white/30 text-white">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {t.contact.form.countries.map((country) => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Project Type (Multi-select) */}
            <div>
              <Label className="text-white mb-3">{t.contact.form.projectType}</Label>
              <div className="grid md:grid-cols-2 gap-3">
                {t.contact.form.projectTypes.map((type) => (
                  <div key={type} className="flex items-center space-x-2 bg-white/10 p-3 rounded-lg">
                    <Checkbox
                      id={type}
                      checked={formData.projectTypes.includes(type)}
                      onCheckedChange={() => handleProjectTypeToggle(type)}
                      className="border-white/50"
                    />
                    <Label htmlFor={type} className="text-white cursor-pointer">{type}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Number of Sites & Urgency */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="numSites" className="text-white mb-2">{t.contact.form.numSites}</Label>
                <Select value={formData.numSites} onValueChange={(value) => setFormData({ ...formData, numSites: value })}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white">
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contact.form.siteOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="urgency" className="text-white mb-2">{t.contact.form.urgency}</Label>
                <Select value={formData.urgency} onValueChange={(value) => setFormData({ ...formData, urgency: value })}>
                  <SelectTrigger className="bg-white/20 border-white/30 text-white">
                    <SelectValue placeholder="Select urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    {t.contact.form.urgencyOptions.map((option) => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-white mb-2">{t.contact.form.message}</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={5}
                className="bg-white/20 border-white/30 text-white placeholder:text-white/60"
              />
            </div>

            {/* Submit */}
            <Button
              type="submit"
              size="lg"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              <Send className="w-5 h-5 mr-2" />
              {t.contact.form.submit}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;